import { useEffect, useMemo, useRef, useState } from "react"

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/vcDialog"

import { Button } from "@/clientWeb/components/ui/button"
import { cn } from "@vc/lib/utils"
import { backgroundCategories, getBackgroundById, type BackgroundImage } from "@vc/data/backgroundCategories"
import { Upload, X, Check, Droplets, Plus, Video } from "lucide-react"
import { ScrollArea } from "@/clientWeb/components/ui/scroll-area"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { blurBackground } from "@vc/data/backgroundCategories"
import { VirtualBackgroundProcessor } from "@/features/meeting/services/virtual-background"
import { useMeetingPreferencesStore } from "@/features/meeting/store/meeting-preferences.store"

interface BackgroundEffectsDialogProps {
  open: boolean
  selectedBackgroundId?: string | null
  /** The applied background itself. Needed for a custom upload, whose image
   *  exists nowhere but this object — an id alone cannot rebuild the tile. */
  selectedBackground?: BackgroundImage | null
  blurIntensity?: number
  customBackground?: string | null
  previewStream?: MediaStream | null
  onClose: () => void
  onSelectBackground: (bg: BackgroundImage) => void
  onClearBackground: () => void
  onBlurChange?: (intensity: number) => void
  onCustomBackgroundUpload?: (imageUrl: string) => void
  /** A tile was picked (not yet applied) — lets the caller start fetching it. */
  onPreviewBackground?: (bg: BackgroundImage) => void
}

export function BackgroundEffectsDialog({
  open,
  selectedBackgroundId,
  selectedBackground = null,
  blurIntensity = 0,
  customBackground = null,
  previewStream,
  onClose,
  onSelectBackground,
  onPreviewBackground,
  onClearBackground,
  onBlurChange,
  onCustomBackgroundUpload,
}: BackgroundEffectsDialogProps) {
  const t = useTranslation();
  /**
   * Flip Camera, so this preview faces the same way the page behind it does.
   *
   * It had no transform at all, so the picker always showed the camera
   * unmirrored — choosing a background while the flip was on meant watching a
   * view that did not match the one you came from, and the one you would go
   * back to.
   */
  const mirrorOwnVideo = useMeetingPreferencesStore((s) => s.mirrorOwnVideo)
  const [localBlurIntensity, setLocalBlurIntensity] = useState(blurIntensity)
  const [hoveredBg, setHoveredBg] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  /** An image the user uploaded this session. Held as a full BackgroundImage
   *  so it travels the same select → Apply → publish path as a bundled one;
   *  `customBackground` seeds it when a caller remembers a previous upload. */
  const [uploadedBackground, setUploadedBackground] = useState<BackgroundImage | null>(
    selectedBackground?.category === 'custom'
      ? selectedBackground
      : customBackground
        ? { id: 'custom-upload', name: 'Custom', category: 'custom', url: customBackground, thumbnail: customBackground }
        : null,
  )

  /** Tentative selection — clicking a tile updates this and the preview
   *  composites against it, but nothing is committed to the lobby until the
   *  user clicks Apply. */
  const [pendingBackgroundId, setPendingBackgroundId] = useState<string | null>(
    selectedBackgroundId ?? null,
  )

  // Re-sync the pending selection whenever the dialog (re)opens or the
  // committed selection changes from the outside.
  useEffect(() => {
    if (!open) return
    setPendingBackgroundId(selectedBackgroundId ?? null)
    // Put an applied custom image back in the grid. State alone does not
    // survive a remount, and without this the tile silently disappears while
    // the background itself is still in use.
    if (selectedBackground?.category === 'custom') setUploadedBackground(selectedBackground)
    // A committed blur comes back as the id `blur-<px>`; without restoring the
    // matching slider position, Apply would see intensity 0 and clear the very
    // blur the user is looking at.
    const px = Number(selectedBackgroundId?.match(/^blur-(\d+)$/)?.[1] ?? NaN)
    if (!Number.isNaN(px)) {
      setLocalBlurIntensity(blurOptions.find((o) => blurPxFor(o.intensity) === px)?.intensity ?? 0)
    }
  }, [open, selectedBackgroundId, selectedBackground])

  // Update raw <video> stream when preview changes — only used when no
  // background is in play (the composited preview path mounts its own video).
  useEffect(() => {
    if (videoRef.current && previewStream) {
      videoRef.current.srcObject = previewStream
    }
  }, [previewStream])

  const pendingBackground = useMemo<BackgroundImage | null>(() => {
    if (!pendingBackgroundId || pendingBackgroundId.startsWith("blur")) return null
    if (uploadedBackground && pendingBackgroundId === uploadedBackground.id) return uploadedBackground
    return getBackgroundById(pendingBackgroundId) ?? null
  }, [pendingBackgroundId, uploadedBackground])

  // Enabled whenever there is something to commit — including re-picking the
  // background that is already applied. Gating on "differs from applied" made
  // the button dead exactly when the user clicked the highlighted tile, which
  // is the natural way to use a picker, and left no way to retry an effect that
  // had failed to take. Re-applying is idempotent.
  const canApply = pendingBackgroundId !== null || (selectedBackgroundId ?? null) !== null

  // Slider positions → real blur radii. The UI values are not pixels.
  const blurPxFor = (intensity: number) =>
    intensity >= 100 ? 20 : intensity >= 50 ? 10 : intensity >= 25 ? 4 : 0

  const blurOptions = [
    { intensity: 0, name: t("dialogs.noBlur"), icon: Droplets, blurClass: "blur-none" },
    { intensity: 25, name: t("dialogs.slightBlur"), icon: Droplets, blurClass: "blur-sm" },
    { intensity: 50, name: t("dialogs.mediumBlur"), icon: Droplets, blurClass: "blur-md" },
    { intensity: 100, name: t("dialogs.heavyBlur"), icon: Droplets, blurClass: "blur-xl" },
  ]


  const selected = useMemo(() => {
    if (!pendingBackgroundId) return null
    if (pendingBackgroundId === "blur-effect") return { id: "blur-effect", name: t("dialogs.backgroundBlur"), category: "blur" }
    for (const c of backgroundCategories) {
      const found = c.backgrounds.find((b) => b.id === pendingBackgroundId)
      if (found) return found
    }
    return null
  }, [pendingBackgroundId, t])

  const isBlurSelected = pendingBackgroundId === "blur-effect" || (pendingBackgroundId?.startsWith("blur-") ?? false)

  // What the pending choice means to the compositor. Null = raw camera.
  const pendingComposite = useMemo<{ url?: string; blurPx?: number } | null>(() => {
    if (isBlurSelected && localBlurIntensity > 0) return { blurPx: blurPxFor(localBlurIntensity) }
    if (pendingBackground?.url) return { url: pendingBackground.url }
    return null
  }, [isBlurSelected, localBlurIntensity, pendingBackground])

  const [previewComposite, setPreviewComposite] = useState<MediaStream | null>(null)

  // Runs the real pipeline over the preview camera. Heavier than a CSS filter,
  // and the point: "see it before you Apply" is worth nothing if the preview
  // and the published result are produced by different code.
  useEffect(() => {
    const track = previewStream?.getVideoTracks()[0]
    if (!open || !track || !pendingComposite) {
      setPreviewComposite(null)
      return
    }
    let cancelled = false
    const processor = new VirtualBackgroundProcessor()
    void processor
      .start(track, pendingComposite)
      .then((out) => {
        if (cancelled) {
          processor.stop()
          return
        }
        setPreviewComposite(new MediaStream([out]))
      })
      .catch(() => {
        // Fall back to the raw camera below rather than a dead rectangle.
        if (!cancelled) setPreviewComposite(null)
      })
    return () => {
      cancelled = true
      processor.stop()
      setPreviewComposite(null)
    }
  }, [open, previewStream, pendingComposite])

  /** Footer actions */
  const handleApply = () => {
    // Blur first: it is a real selection now, not the absence of one.
    if (isBlurSelected && localBlurIntensity > 0) {
      const px = blurPxFor(localBlurIntensity)
      const label = blurOptions.find((o) => o.intensity === localBlurIntensity)?.name ?? 'Blur'
      onSelectBackground(blurBackground(px, label))
      return
    }
    if (!pendingBackground) {
      onClearBackground()
      return
    }
    onSelectBackground(pendingBackground)
  }

  const handleCancel = () => {
    setPendingBackgroundId(selectedBackgroundId ?? null)
    onClose()
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset so re-picking the same file fires a change event again.
    event.target.value = ''
    if (!file) return
    try {
      // Downscaled before it becomes a data URL. A phone photo is several MB,
      // and the selected background is persisted to localStorage (~5MB total)
      // so the choice survives a reload — a full-size upload would blow that
      // quota and silently lose every stored preference, not just this one.
      const url = await downscaleToDataUrl(file, OUTPUT_MAX_EDGE)
      // The downscaled image doubles as its own thumbnail — it is already small.
      const bg: BackgroundImage = {
        id: `custom-${Date.now()}`,
        name: 'Custom',
        category: 'custom',
        url,
        thumbnail: url,
      }
      setUploadedBackground(bg)
      setPendingBackgroundId(bg.id)
      setLocalBlurIntensity(0)
      onCustomBackgroundUpload?.(url)
    } catch {
      // A file the browser cannot decode isn't an image; leave the selection be.
    }
  }

  const handleCustomUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleSelectBackground = (bg: BackgroundImage) => {
    setPendingBackgroundId(bg.id)
    onPreviewBackground?.(bg)
    // An image and a blur are mutually exclusive; leaving the slider parked at
    // a non-zero value would make the blur strip look co-selected.
    setLocalBlurIntensity(0)
  }

  const handleBlurSelect = (intensity: number) => {
    setLocalBlurIntensity(intensity)
    // Blur and image are alternatives, so picking one clears the other. Without
    // this the blur buttons only moved a local number: `handleApply` looked for
    // a pending IMAGE, found none, and cleared the background instead.
    setPendingBackgroundId(intensity > 0 ? `blur-${blurPxFor(intensity)}` : null)
    onBlurChange?.(intensity)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent
        maxWidthClass="max-w-[600px]"
        className="w-full max-h-[90vh] sm:rounded-2xl p-0 gap-0 overflow-hidden bg-background flex flex-col"
      >
        <DialogHeader className="flex-shrink-0 px-5 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-semibold">{t("dialogs.backgroundEffects")}</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                {t("dialogs.chooseBackgroundForVideo")}
              </DialogDescription>
            </div>
            {/* "Remove background", not "Remove".
                This is the only way to clear a background, and it sits in the
                HEADER — away from the grid of tiles it acts on — so a bare
                "Remove" gave no indication of what it removed. Next to a list
                of backgrounds, several of them custom, it reads just as easily
                as "remove this background from the list", which is the one
                thing it does not do.

                `title` and `aria-label` carry the same words rather than
                shorter ones: the label is clipped to `text-xs` beside a dialog
                title, and it is what a screen reader announces. */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPendingBackgroundId(null)}
              disabled={!pendingBackgroundId}
              title={t("dialogs.removeBackground")}
              aria-label={t("dialogs.removeBackground")}
              className="text-xs h-8"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              {t("dialogs.removeBackground")}
            </Button>
          </div>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0 overflow-hidden py-4">
          {/* Left Side - Video Preview */}
          <div className="flex-shrink-0 px-5 border-b lg:border-b-0 bg-muted/20">
            <div className="flex flex-col h-full">

              {/* Video Preview Area — composites live camera against the
                  *pending* background so the user can see the result before
                  committing via Apply. */}
              <div className="relative flex-1 min-h-[140px] bg-gray-900 rounded-xl overflow-hidden">
                {previewStream ? (
                  previewComposite ? (
                    // The composited result, from the same processor that
                    // publishes — image or blur, exactly as the room will see it.
                    <PreviewVideo stream={previewComposite} mirrored={mirrorOwnVideo} />
                  ) : (
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      style={{ transform: mirrorOwnVideo ? "scaleX(-1)" : undefined }}
                    />
                  )
                ) : (
                  /* No camera to preview against. The icon alone left people
                     looking at a grey circle with nothing to explain it, so say
                     what is missing — this is reachable from the lobby too,
                     where the camera can be off for the same reason. */
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
                    <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center">
                      <Video className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-300">
                      {t("controlBar.cameraOffForBackgrounds")}
                    </p>
                  </div>
                )}

                {/* Blur intensity indicator overlay — only when no image bg is queued. */}
                {!pendingBackground && localBlurIntensity > 0 && (
                  <div className="absolute top-3 left-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                    {blurOptions.find(o => o.intensity === localBlurIntensity)?.name}
                  </div>
                )}
              </div>

              {/* Selected Info */}
              <div className="mt-3 text-sm">
                {selected ? (
                  <span className="text-muted-foreground">
                    {t("dialogs.selected")}: <span className="font-medium text-foreground">{selected.name}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t("dialogs.noBackgroundSelected")}</span>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Options */}
          <div className="flex-1 min-h-0 flex overflow-y-auto">
            <ScrollArea className="h-full">
              <div className="p-5 space-y-6">
                {/* Blur Section - With actual preview images */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Droplets className="h-4 w-4" />
                      {t("dialogs.backgroundBlur")}
                    </h3>
                  </div>

                  {/* Blur Options as preview images */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {blurOptions.map((option) => {
                      const isSelected = localBlurIntensity === option.intensity && isBlurSelected
                      return (
                        <button
                          key={option.intensity}
                          type="button"
                          onClick={() => handleBlurSelect(option.intensity)}
                          className={cn(
                            "group relative aspect-[4/3] rounded-lg overflow-hidden transition-all duration-200",
                            "border-2 hover:border-primary/50",
                            isSelected
                              ? "border-primary ring-2 ring-primary/20"
                              : "border-border"
                          )}
                        >
                          {/* Blur preview using CSS gradient pattern (no external dependency) */}
                          <div
                            className="absolute inset-0"
                            style={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
                            }}
                          >
                            {/* Apply blur based on intensity */}
                            <div
                              className="absolute inset-0 transition-all"
                              style={{
                                filter: option.intensity === 0 ? 'none' : `blur(${option.intensity / 10}px)`,
                                background: 'inherit',
                              }}
                            />
                          </div>

                          {/* Icon overlay */}
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-t from-black/60 via-black/20 to-transparent">
                            <option.icon
                              className={cn(
                                "h-6 w-6 transition-all drop-shadow-lg",
                                option.intensity === 0 ? "text-white/70" : "text-white",
                                isSelected && "scale-110"
                              )}
                            />
                            <span className={cn(
                              "text-xs font-medium text-white drop-shadow-md",
                              isSelected && "text-primary-300"
                            )}>
                              {option.name}
                            </span>
                          </div>

                          {/* Selection indicator */}
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                              <Check className="h-3 w-3 text-primary-foreground" />
                            </div>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </section>

                {/* Background Categories */}
                {backgroundCategories.map((category) => (
                  <section key={category.id}>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <span>{category.icon}</span>
                      {category.name}
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {category.backgrounds.map((bg) => {
                        const isSelected = bg.id === pendingBackgroundId
                        return (
                          <button
                            key={bg.id}
                            type="button"
                            onClick={() => handleSelectBackground(bg)}
                            onMouseEnter={() => setHoveredBg(bg.id)}
                            onMouseLeave={() => setHoveredBg(null)}
                            className={cn(
                              "group relative aspect-square rounded-lg overflow-hidden transition-all duration-200",
                              "border-2 hover:border-primary/50 hover:scale-[1.02]",
                              isSelected
                                ? "border-primary ring-2 ring-primary/20"
                                : "border-border"
                            )}
                          >
                            <img
                              src={bg.thumbnail}
                              alt={bg.name}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                            {isSelected && (
                              <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                                <Check className="h-3 w-3 text-primary-foreground" />
                              </div>
                            )}
                            {hoveredBg === bg.id && (
                              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1">
                                <span className="text-[10px] text-white font-medium line-clamp-1">{bg.name}</span>
                              </div>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </section>
                ))}

                {/* Custom Upload Section */}
                <section>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    {t("dialogs.custom")}
                  </h3>
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                    {/* Custom Upload Button */}
                    <button
                      type="button"
                      onClick={handleCustomUploadClick}
                      className={cn(
                        "group relative aspect-square rounded-lg overflow-hidden transition-all duration-200",
                        "border-2 border-dashed border-border hover:border-primary/50 hover:scale-[1.02]",
                        "bg-muted/50 flex flex-col items-center justify-center gap-1"
                      )}
                    >
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <span className="text-[10px] text-muted-foreground">{t("dialogs.upload")}</span>
                    </button>

                    {/* Show custom background if uploaded */}
                    {uploadedBackground && (
                      <button
                        type="button"
                        onClick={() => handleSelectBackground(uploadedBackground)}
                        className={cn(
                          "group relative aspect-square rounded-lg overflow-hidden transition-all duration-200",
                          "border-2 hover:border-primary/50 hover:scale-[1.02]",
                          pendingBackgroundId === uploadedBackground.id
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-border"
                        )}
                      >
                        <img
                          src={uploadedBackground.url}
                          alt={t("dialogs.customBackground")}
                          className="h-full w-full object-cover"
                        />
                        {pendingBackgroundId === uploadedBackground.id && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </button>
                    )}
                  </div>
                </section>
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Footer — Cancel + Apply. Tile clicks only update the preview;
            nothing reaches the lobby until the user confirms here. */}
        <div className="flex-shrink-0 flex items-center justify-end gap-2 px-5 py-3 border-t bg-background">
          <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canApply}
            onClick={handleApply}
            className="min-w-[88px]"
          >
            {t("common.apply")}
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
      </DialogContent>
    </Dialog>
  )
}

/** Binds a MediaStream to a <video>. The dialog's preview is a plain stream now
 *  — the compositing happens upstream, in the same processor that publishes. */
function PreviewVideo({ stream, mirrored }: { stream: MediaStream; mirrored: boolean }) {
  const ref = useRef<HTMLVideoElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.srcObject = stream
    void el.play().catch(() => {})
    return () => {
      el.srcObject = null
    }
  }, [stream])
  return (
    <video
      ref={ref}
      autoPlay
      muted
      playsInline
      className="w-full h-full object-cover"
      style={{ transform: mirrored ? "scaleX(-1)" : undefined }}
    />
  )
}

/** Longest edge of a stored custom background. Matches the compositor's output
 *  height, so nothing is gained by keeping more. */
const OUTPUT_MAX_EDGE = 1280

/** Decodes, downscales and re-encodes an uploaded image as a JPEG data URL. */
function downscaleToDataUrl(file: File, maxEdge: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(img.width * scale)
      canvas.height = Math.round(img.height * scale)
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('background upload: no 2d context'))
        return
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      // JPEG, not PNG: a PNG screenshot re-encodes to several MB and this
      // string goes into localStorage.
      resolve(canvas.toDataURL('image/jpeg', 0.85))
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('background upload: not a decodable image'))
    }
    img.src = objectUrl
  })
}
